/**
 * Reporting & Exports (`/insights/reports`) — pure, unit-testable aggregation of the read-models
 * already published by other modules (`insights` widgets, `case_.Case`, `case_.ProgramFiling`,
 * `credit.CreditInstrument`). No new backend endpoint: every number here is assembled client-side
 * from data the app already fetches elsewhere (CLAUDE.md "config, not code" / module-boundary
 * invariants are untouched — this is a read-only, cross-module *view*, the same posture as the
 * `insights` BFF's own assemblers, just composed in the browser instead of the server).
 *
 * Money/label conventions mirror `screens/insights/format.ts` and `lib/labels.ts` — a business
 * label or a formatted amount is always the display value; a raw code/UUID is never rendered.
 */
import { label } from '../../lib/labels'
import type { CreditValueBucket } from '../../types/insights'
import type { CreditInstrumentView } from '../../types/credit'
import type { ProgramFilingListView } from '../../types/programFiling'

export interface JurisdictionCreditTotal {
  jurisdiction: string
  label: string
  total: number
}

/** Sums `CreditValueBucket.sumCertifiedAmount` across fiscal years, per jurisdiction — descending by total. */
export function aggregateCertifiedByJurisdiction(
  buckets: ReadonlyArray<CreditValueBucket>,
): JurisdictionCreditTotal[] {
  const totals = new Map<string, number>()
  for (const bucket of buckets) {
    const amount = Number(bucket.sumCertifiedAmount)
    if (!Number.isFinite(amount)) continue
    totals.set(bucket.jurisdiction, (totals.get(bucket.jurisdiction) ?? 0) + amount)
  }
  return [...totals.entries()]
    .map(([jurisdiction, total]) => ({ jurisdiction, label: label('jurisdiction', jurisdiction), total }))
    .sort((a, b) => b.total - a.total)
}

export interface CaseStatusCount {
  status: string
  label: string
  count: number
}

/** Groups `CaseListView.overallStatus` client-side — descending by count. */
export function aggregateCaseStatusCounts(cases: ReadonlyArray<{ overallStatus: string }>): CaseStatusCount[] {
  const counts = new Map<string, number>()
  for (const c of cases) {
    counts.set(c.overallStatus, (counts.get(c.overallStatus) ?? 0) + 1)
  }
  return [...counts.entries()]
    .map(([status, count]) => ({ status, label: label('caseStatus', status), count }))
    .sort((a, b) => b.count - a.count)
}

export interface TopClientCredit {
  accountId: string
  accountName: string
  currency: string
  certifiedTotal: number
}

/**
 * Zips a page of `ProgramFilingListView` rows with their (index-aligned) resolved
 * `CreditInstrumentView | null` and sums certified amounts per (account, currency) — never summed
 * across currencies (same "never comingle currencies" posture as `OutcomeVarianceWidget`'s
 * per-currency buckets). Filings with no account (a genuine data-model gap, see
 * `types/programFiling.ts`) or no certified credit instrument yet are excluded, not zero-filled.
 * Descending by amount, capped to `limit`.
 */
export function aggregateTopClientsByCredit(
  filings: ReadonlyArray<ProgramFilingListView>,
  instruments: ReadonlyArray<CreditInstrumentView | null>,
  limit: number,
): TopClientCredit[] {
  const byKey = new Map<string, TopClientCredit>()
  filings.forEach((filing, index) => {
    const instrument = instruments[index]
    if (!instrument || !filing.accountId) return
    const key = `${filing.accountId}::${instrument.currency}`
    const existing = byKey.get(key)
    if (existing) {
      existing.certifiedTotal += instrument.certifiedAmount
    } else {
      byKey.set(key, {
        accountId: filing.accountId,
        accountName: filing.accountName ?? filing.accountId,
        currency: instrument.currency,
        certifiedTotal: instrument.certifiedAmount,
      })
    }
  })
  return [...byKey.values()].sort((a, b) => b.certifiedTotal - a.certifiedTotal).slice(0, limit)
}
